import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CalendarCheckIcon, CrosshairIcon, UsersIcon, WalletCardsIcon } from 'lucide-react';
import type { SlideProps } from './SlideShell';
interface Commitment {
  icon: typeof CrosshairIcon;
  title: string;
  description: string;
}
const COMMITMENTS: Commitment[] = [{
  icon: CrosshairIcon,
  title: 'Choose one priority',
  description: 'Select the brand, HCP audience, and first use case for the Netherlands pilot.'
}, {
  icon: UsersIcon,
  title: 'Connect both teams',
  description: 'Name the medical and commercial owners who will shape one continuous journey.'
}, {
  icon: WalletCardsIcon,
  title: 'Confirm the path to scale',
  description: 'Validate North Europe interest and budget, then map expansion through the MCO.'
}];
export function SlideFinalCTA({}: SlideProps) {
  return <div className="relative flex h-full w-full overflow-hidden bg-[var(--fg)] px-[clamp(2rem,6vw,6rem)] py-[clamp(2rem,5vw,5rem)] text-white" data-id="element-203" data-mp="3d2xn1l5mlwy5">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="absolute left-0 top-0 h-2 w-full origin-left bg-[var(--brand)]" data-id="element-204" data-mp="1cciaus00xeaw" />
      <div className="pointer-events-none absolute -right-32 -top-32 h-[34rem] w-[34rem] rounded-full border-[64px] border-white/[0.035]" data-id="element-205" data-mp="1q22avq27zxgi" />
      <div className="pointer-events-none absolute -bottom-28 left-[36%] h-72 w-72 rounded-full border-[42px] border-[var(--brand)]/10" data-id="element-206" data-mp="1v03ucc3k5ysi" />

      <div className="relative z-10 flex w-full flex-col justify-between" data-id="element-207" data-mp="202cnp9kcf2v5">
        <motion.header initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55
      }} className="max-w-6xl" data-id="element-208" data-mp="242d2wwf9ni39">
          <div className="mb-5 flex items-center gap-3 text-[var(--brand-lighter)]" data-id="element-209" data-mp="9p3yvk5hoose">
            <span className="h-px w-12 bg-[var(--brand-light)]" data-id="element-210" data-mp="enyi2n7dcfd1" />
            <p className="text-xs font-bold uppercase tracking-[0.25em]" data-id="element-211" data-mp="1hxt0i8wau4q8">
              The next decision
            </p>
          </div>
          <h1 className="text-[clamp(3rem,6.8vw,6.8rem)] font-extrabold leading-[0.94] tracking-[-0.055em]" data-id="element-212" data-mp="1czrlbz1zupk1">
            Let’s put the Netherlands
            <span className="block text-[var(--brand-light)]" data-id="element-213" data-mp="1qw4tghkn1elu">pilot in motion.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-[clamp(1rem,1.65vw,1.4rem)] font-medium leading-relaxed text-white/65" data-id="element-214" data-mp="1m1r34tku1xo1">
            One focused working session can turn the opportunity into a scoped,
            measurable pilot—and give North Europe a live model to scale.
          </p>
        </motion.header>

        <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-3" data-id="element-215" data-mp="25ndmz1rnvk49">
          {COMMITMENTS.map((commitment, index) => {
          const Icon = commitment.icon;
          return <motion.article key={commitment.title} initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.28 + index * 0.1,
            duration: 0.5
          }} className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm" data-id="element-216" data-mp="2jktu6fhs7hx2" data-mpi={index}>
                <div className="flex items-start gap-4" data-id="element-217" data-mp="2oaw9lurj782h">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--brand)] text-white" data-id="element-218" data-mp="2tash43zfthms">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-219" data-mp="2fichze9nq3r5" />
                  </span>
                  <div data-id="element-220" data-mp="2kacbanv81yer">
                    <p className="text-base font-bold" data-id="element-221" data-mp="2kbnh0t889g8i">{commitment.title}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-white/60" data-id="element-222" data-mp="25m96hhsx0srj">
                      {commitment.description}
                    </p>
                  </div>
                </div>
              </motion.article>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.72,
        duration: 0.55
      }} className="mt-5 flex flex-wrap items-center justify-between gap-5 border-t border-white/10 pt-5" data-id="element-223" data-mp="20m3jwg1i5wgb">
          <div className="flex items-center gap-3" data-id="element-224" data-mp="1olwwljp8cjif">
            <CalendarCheckIcon className="h-5 w-5 text-[var(--brand-light)]" aria-hidden="true" data-id="element-225" data-mp="1jsbv3hofh7eh" />
            <p className="text-sm font-semibold text-white/75" data-id="element-226" data-mp="1eushmjzk0ynl">
              Aizhan validates North Europe interest and budget availability.
            </p>
          </div>
          <div className="inline-flex items-center gap-3 rounded-full bg-[var(--brand)] px-6 py-3.5 text-sm font-extrabold shadow-lg shadow-black/20" data-id="element-227" data-mp="15tlye35it9qn">
            Schedule the pilot scoping session
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-228" data-mp="vvejrqypf3rt" />
          </div>
        </motion.div>
      </div>
    </div>;
}